import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ChevronRight, ChevronDown, Pencil, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { JacKeyIcon } from './JacKeyIcon';
import { useUserCredits } from '@/hooks/useUserCredits';
import { getAuthHeaders } from '@/lib/authHeaders';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { Language } from '@/i18n/types';
import type { AnnouncementFormData, TemplateType } from '@/types/announcement';

interface JacKeyGuideV2Props {
  formData: AnnouncementFormData;
  updateFormData: (updates: Partial<AnnouncementFormData>) => void;
  onDismiss: () => void;
}

type JacKeyMode = 'quick' | 'deep' | 'rewrite';
type LengthOption = 'short' | 'standard' | 'long' | 'keepsake';
type FlowStep = 'collapsed' | 'intro' | 'mode_select' | 'questions' | 'generating' | 'drafts' | 'refine' | 'complete';

import { INTENT_DATA, resolveIntentSlug, lstr as lstrIntent, lstrArr, type IntentSlug } from './jackey-intents';

// Helper to get localized string from a record with language keys
function lstr(dict: Record<string, string>, lang: Language): string {
  return dict[lang] ?? dict.en;
}

const TEMPLATE_INTROS: Partial<Record<TemplateType, Record<string, string>>> = {
  gratitude: {
    en: "Gratitude feels simple — but the right words make it powerful.",
    es: "La gratitud parece simple — pero las palabras correctas la hacen poderosa.",
    fr: "La gratitude semble simple — mais les bons mots la rendent puissante.",
    pt: "Gratidão parece simples — mas as palavras certas a tornam poderosa.",
    ar: "الامتنان يبدو بسيطاً — لكن الكلمات الصحيحة تجعله قوياً.",
    ja: "感謝はシンプルに見えますが、適切な言葉がその力を引き出します。",
    ko: "감사는 단순해 보이지만, 적절한 말이 그 힘을 만듭니다.",
    id: "Rasa syukur terasa sederhana — tetapi kata-kata yang tepat membuatnya kuat.",
  },
  apology: {
    en: "Some words feel heavy. Let's unlock them gently.",
    es: "Algunas palabras pesan. Vamos a desbloquearlas con suavidad.",
    fr: "Certains mots pèsent lourd. Déverrouillons-les en douceur.",
    pt: "Algumas palavras pesam. Vamos desbloqueá-las com delicadeza.",
    ar: "بعض الكلمات ثقيلة. دعنا نفتحها بلطف.",
    ja: "重い言葉もあります。優しく解き放ちましょう。",
    ko: "어떤 말은 무겁게 느껴집니다. 부드럽게 풀어봅시다.",
    id: "Beberapa kata terasa berat. Mari kita buka dengan lembut.",
  },
  love: {
    en: "Love grows when spoken.",
    es: "El amor crece cuando se expresa.",
    fr: "L'amour grandit quand on l'exprime.",
    pt: "O amor cresce quando é expresso.",
    ar: "الحب ينمو عندما يُعبَّر عنه.",
    ja: "愛は言葉にすると育ちます。",
    ko: "사랑은 말로 표현할 때 자랍니다.",
    id: "Cinta tumbuh saat diungkapkan.",
  },
  closure: {
    en: "Closure isn't giving up — it's choosing peace.",
    es: "El cierre no es rendirse — es elegir la paz.",
    fr: "Le dénouement, ce n'est pas abandonner — c'est choisir la paix.",
    pt: "Encerrar não é desistir — é escolher a paz.",
    ar: "الإغلاق ليس استسلاماً — إنه اختيار السلام.",
    ja: "区切りをつけることは諦めではなく、平和を選ぶことです。",
    ko: "마무리는 포기가 아닙니다 — 평화를 선택하는 것입니다.",
    id: "Penutupan bukan menyerah — ini memilih kedamaian.",
  },
  hard_conversation: {
    en: "Hard conversations deserve care. Let's find the right words together.",
    es: "Las conversaciones difíciles merecen cuidado. Encontremos las palabras juntos.",
    fr: "Les conversations difficiles méritent du soin. Trouvons les mots ensemble.",
    pt: "Conversas difíceis merecem cuidado. Vamos encontrar as palavras juntos.",
    ar: "المحادثات الصعبة تستحق العناية. لنجد الكلمات المناسبة معاً.",
    ja: "難しい会話には思いやりが必要です。一緒に適切な言葉を見つけましょう。",
    ko: "어려운 대화는 배려가 필요합니다. 함께 적절한 말을 찾아봅시다.",
    id: "Percakapan sulit membutuhkan kehati-hatian. Mari temukan kata-kata yang tepat bersama.",
  },
  custom: {
    en: "Every message deserves intention. Let's make this one matter.",
    es: "Cada mensaje merece intención. Hagamos que este importe.",
    fr: "Chaque message mérite une intention. Faisons en sorte que celui-ci compte.",
    pt: "Toda mensagem merece intenção. Vamos fazer esta valer a pena.",
    ar: "كل رسالة تستحق النية. لنجعل هذه الرسالة مهمة.",
    ja: "すべてのメッセージには意図があるべきです。このメッセージを大切にしましょう。",
    ko: "모든 메시지는 의도가 필요합니다. 이 메시지를 의미 있게 만들어봅시다.",
    id: "Setiap pesan layak mendapat niat. Mari buat yang ini berarti.",
  },
  birthday: {
    en: "Say something they will remember long after today.",
    es: "Di algo que recordarán mucho después de hoy.",
    fr: "Dites quelque chose dont ils se souviendront longtemps.",
    pt: "Diga algo que eles vão lembrar muito depois de hoje.",
    ar: "قل شيئاً سيتذكرونه طويلاً بعد اليوم.",
    ja: "今日の後もずっと覚えていてもらえる言葉を。",
    ko: "오늘이 지나도 오래 기억될 말을 전하세요.",
    id: "Katakan sesuatu yang akan mereka ingat lama setelah hari ini.",
  },
  anniversary: {
    en: "Honor the story only the two of you share.",
    es: "Honra la historia que solo ustedes dos comparten.",
    fr: "Honorez l'histoire que vous seuls partagez.",
    pt: "Honre a história que só vocês dois compartilham.",
    ar: "كرّم القصة التي تشاركانها أنتما فقط.",
    ja: "二人だけが共有する物語を称えましょう。",
    ko: "둘만이 공유하는 이야기를 기리세요.",
    id: "Hormati kisah yang hanya kalian berdua yang tahu.",
  },
  celebration: {
    en: "Make them feel seen, celebrated, and unforgettable.",
    es: "Hazlos sentir vistos, celebrados e inolvidables.",
    fr: "Faites-les se sentir vus, célébrés et inoubliables.",
    pt: "Faça-os se sentir vistos, celebrados e inesquecíveis.",
    ar: "اجعلهم يشعرون بأنهم مرئيون ومحتفى بهم.",
    ja: "見てもらえている、祝われていると感じさせましょう。",
    ko: "인정받고, 축하받고, 잊을 수 없다고 느끼게 하세요.",
    id: "Buat mereka merasa dilihat, dirayakan, dan tak terlupakan.",
  },
};

// Relationship-specific intro overrides
const RELATIONSHIP_INTROS: Partial<Record<RelationshipKey, Record<string, string>>> = {
  crush: {
    en: "Feelings this real deserve more than just a text. Let's shape something honest.",
    es: "Sentimientos tan reales merecen más que un texto. Demos forma a algo honesto.",
    fr: "Des sentiments aussi vrais méritent plus qu'un simple texte. Créons quelque chose d'honnête.",
    pt: "Sentimentos tão reais merecem mais que uma mensagem. Vamos moldar algo honesto.",
    ar: "مشاعر حقيقية كهذه تستحق أكثر من مجرد رسالة. لنصنع شيئاً صادقاً.",
    ja: "こんなに本物の気持ちは、ただのメッセージ以上のものに値します。",
    ko: "이렇게 진짜인 감정은 단순한 문자 이상의 것을 받을 자격이 있어요.",
    id: "Perasaan senyata ini layak mendapat lebih dari sekadar pesan. Mari bentuk sesuatu yang jujur.",
  },
  parent: {
    en: "Parents carry more than we ever see. Let's honor that.",
    es: "Los padres cargan más de lo que vemos. Honremos eso.",
    fr: "Les parents portent plus qu'on ne le voit. Honorons cela.",
    pt: "Os pais carregam mais do que vemos. Vamos honrar isso.",
    ar: "الآباء يحملون أكثر مما نرى. لنكرم ذلك.",
    ja: "親は私たちが思う以上のものを背負っています。それを称えましょう。",
    ko: "부모님은 우리가 보는 것 이상으로 많은 것을 감당합니다. 그것을 기리겠습니다.",
    id: "Orang tua menanggung lebih dari yang kita lihat. Mari hormati itu.",
  },
  grandparent: {
    en: "Legacy lives in stories. Let's capture one.",
    es: "El legado vive en las historias. Capturemos una.",
    fr: "L'héritage vit dans les histoires. Capturons-en une.",
    pt: "O legado vive nas histórias. Vamos capturar uma.",
    ar: "الإرث يعيش في القصص. لنسجل واحدة.",
    ja: "遺産は物語の中に生きています。一つ残しましょう。",
    ko: "유산은 이야기 속에 살아 있습니다. 하나를 담아봅시다.",
    id: "Warisan hidup dalam cerita. Mari abadikan satu.",
  },
  spouse: {
    en: "Love grows when spoken.",
    es: "El amor crece cuando se expresa.",
    fr: "L'amour grandit quand on l'exprime.",
    pt: "O amor cresce quando é expresso.",
    ar: "الحب ينمو عندما يُعبَّر عنه.",
    ja: "愛は言葉にすると育ちます。",
    ko: "사랑은 말로 표현할 때 자랍니다.",
    id: "Cinta tumbuh saat diungkapkan.",
  },
  friend: {
    en: "Friendship is showing up with words, not just presence.",
    es: "La amistad es presentarse con palabras, no solo con presencia.",
    fr: "L'amitié, c'est être présent avec des mots, pas seulement avec sa présence.",
    pt: "Amizade é se fazer presente com palavras, não só com presença.",
    ar: "الصداقة هي الحضور بالكلمات، وليس فقط بالوجود.",
    ja: "友情とは、存在だけでなく言葉で寄り添うことです。",
    ko: "우정은 존재뿐 아니라 말로 함께하는 것입니다.",
    id: "Persahabatan adalah hadir dengan kata-kata, bukan hanya kehadiran.",
  },
  child: {
    en: "Your words shape who they become. Let's make them count.",
    es: "Tus palabras moldean quiénes serán. Hagamos que cuenten.",
    fr: "Vos mots façonnent qui ils deviennent. Faisons en sorte qu'ils comptent.",
    pt: "Suas palavras moldam quem eles se tornam. Vamos fazer valer a pena.",
    ar: "كلماتك تشكّل من سيكونون. لنجعلها مهمة.",
    ja: "あなたの言葉が彼らの未来を形作ります。大切にしましょう。",
    ko: "당신의 말이 그들의 미래를 만듭니다. 의미 있게 만들어봅시다.",
    id: "Kata-kata Anda membentuk siapa mereka. Mari buat kata-kata itu berarti.",
  },
};

// Template-specific question banks — use lstr() arrays pattern
const TEMPLATE_QUESTIONS: Record<TemplateType, Record<string, string[]>> = {
  hard_conversation: {
    en: ["What feels hardest to say—specifically?", "What do you want them to understand about your intent?", "What do you want to protect in the relationship?", "What's one line you do NOT want included?"],
    es: ["¿Qué se siente más difícil de decir, específicamente?", "¿Qué quieres que entiendan sobre tu intención?", "¿Qué quieres proteger en la relación?", "¿Qué es una línea que NO quieres incluir?"],
    fr: ["Qu'est-ce qui est le plus difficile à dire, précisément ?", "Que voulez-vous qu'ils comprennent sur votre intention ?", "Que voulez-vous protéger dans la relation ?", "Quelle phrase ne voulez-vous PAS inclure ?"],
    pt: ["O que parece mais difícil de dizer, especificamente?", "O que você quer que entendam sobre sua intenção?", "O que você quer proteger na relação?", "Qual frase você NÃO quer incluir?"],
    ar: ["ما أصعب شيء لقوله تحديداً؟", "ماذا تريدهم أن يفهموا عن نيتك؟", "ماذا تريد أن تحمي في العلاقة؟", "ما الجملة التي لا تريد تضمينها؟"],
    ja: ["具体的に、一番言いにくいことは何ですか？", "あなたの意図について何を理解してほしいですか？", "この関係で何を守りたいですか？", "含めたくない言葉はありますか？"],
    ko: ["구체적으로, 가장 말하기 어려운 것은 무엇인가요?", "당신의 의도에 대해 무엇을 이해해 주길 바라나요?", "이 관계에서 무엇을 지키고 싶나요?", "포함하고 싶지 않은 말이 있나요?"],
    id: ["Apa yang paling sulit untuk dikatakan, secara spesifik?", "Apa yang Anda ingin mereka pahami tentang niat Anda?", "Apa yang ingin Anda lindungi dalam hubungan ini?", "Kalimat apa yang TIDAK ingin Anda sertakan?"],
  },
  gratitude: {
    en: ["What did they do that changed something for you?", "How would your life be different without them?", "Have you ever told them this before?", "Do you want it simple or deeply emotional?"],
    es: ["¿Qué hicieron que cambió algo para ti?", "¿Cómo sería tu vida sin ellos?", "¿Alguna vez les has dicho esto antes?", "¿Lo quieres simple o profundamente emocional?"],
    fr: ["Qu'ont-ils fait qui a changé quelque chose pour vous ?", "Comment serait votre vie sans eux ?", "Leur avez-vous déjà dit cela ?", "Voulez-vous que ce soit simple ou profondément émotionnel ?"],
    pt: ["O que eles fizeram que mudou algo para você?", "Como seria sua vida sem eles?", "Você já disse isso antes?", "Quer algo simples ou profundamente emocional?"],
    ar: ["ماذا فعلوا مما غيّر شيئاً فيك؟", "كيف ستكون حياتك بدونهم؟", "هل أخبرتهم بهذا من قبل؟", "هل تريدها بسيطة أم عاطفية بعمق؟"],
    ja: ["あなたにとって何を変えてくれましたか？", "彼らがいなかったら人生はどう違いましたか？", "これを伝えたことはありますか？", "シンプルにしたいですか、それとも深く感情的に？"],
    ko: ["그들이 당신에게 어떤 변화를 가져왔나요?", "그들이 없었다면 삶이 어떻게 달랐을까요?", "이전에 이 말을 전한 적이 있나요?", "단순하게 하고 싶나요, 아니면 깊은 감정으로?"],
    id: ["Apa yang mereka lakukan yang mengubah sesuatu bagi Anda?", "Bagaimana hidup Anda tanpa mereka?", "Pernahkah Anda menyampaikan ini sebelumnya?", "Ingin yang sederhana atau sangat emosional?"],
  },
  apology: {
    en: ["What do you wish you had handled differently?", "What responsibility are you ready to own?", "If they forgave you today, what would change for you?", "What change can you commit to?"],
    es: ["¿Qué desearías haber manejado de otra manera?", "¿De qué responsabilidad estás listo para hacerte cargo?", "Si te perdonaran hoy, ¿qué cambiaría para ti?", "¿A qué cambio te puedes comprometer?"],
    fr: ["Qu'auriez-vous aimé gérer différemment ?", "Quelle responsabilité êtes-vous prêt à assumer ?", "S'ils vous pardonnaient aujourd'hui, qu'est-ce qui changerait ?", "À quel changement pouvez-vous vous engager ?"],
    pt: ["O que gostaria de ter feito diferente?", "Que responsabilidade está pronto para assumir?", "Se te perdoassem hoje, o que mudaria?", "A que mudança pode se comprometer?"],
    ar: ["ما الذي تمنيت لو تعاملت معه بشكل مختلف؟", "ما المسؤولية التي أنت مستعد لتحملها؟", "لو سامحوك اليوم، ماذا سيتغير؟", "ما التغيير الذي يمكنك الالتزام به؟"],
    ja: ["どう対処すればよかったと思いますか？", "どんな責任を引き受ける準備がありますか？", "もし今日許されたら、何が変わりますか？", "どんな変化にコミットできますか？"],
    ko: ["다르게 처리했으면 하는 것은 무엇인가요?", "어떤 책임을 질 준비가 되었나요?", "오늘 용서받는다면, 무엇이 달라질까요?", "어떤 변화에 전념할 수 있나요?"],
    id: ["Apa yang Anda harap ditangani secara berbeda?", "Tanggung jawab apa yang siap Anda ambil?", "Jika mereka memaafkan Anda hari ini, apa yang berubah?", "Perubahan apa yang bisa Anda janjikan?"],
  },
  love: {
    en: ["What did they bring into your life that didn't exist before?", "What do they do that makes you feel safe?", "What do you hope they never doubt?", "Do you want playful, romantic, or deeply sincere?"],
    es: ["¿Qué trajeron a tu vida que no existía antes?", "¿Qué hacen que te hace sentir seguro/a?", "¿Qué esperas que nunca duden?", "¿Lo quieres juguetón, romántico o profundamente sincero?"],
    fr: ["Qu'ont-ils apporté dans votre vie qui n'existait pas avant ?", "Que font-ils qui vous fait sentir en sécurité ?", "Qu'espérez-vous qu'ils ne doutent jamais ?", "Voulez-vous que ce soit ludique, romantique ou profondément sincère ?"],
    pt: ["O que trouxeram para sua vida que não existia antes?", "O que fazem que te faz sentir seguro/a?", "O que espera que nunca duvidem?", "Quer algo divertido, romântico ou profundamente sincero?"],
    ar: ["ماذا أحضروا لحياتك ولم يكن موجوداً من قبل؟", "ماذا يفعلون ليجعلوك تشعر بالأمان؟", "ما الذي تأمل ألا يشكوا فيه أبداً؟", "هل تريدها مرحة، رومانسية أم صادقة بعمق؟"],
    ja: ["あなたの人生に、以前にはなかった何をもたらしてくれましたか？", "何をしてくれると安心しますか？", "彼らに絶対に疑ってほしくないことは？", "遊び心、ロマンチック、深く誠実のどれがいいですか？"],
    ko: ["이전에 없던 무엇을 삶에 가져다 주었나요?", "무엇을 해서 안전하다고 느끼게 하나요?", "절대 의심하지 않았으면 하는 것은?", "장난스럽게, 로맨틱하게, 깊이 진심으로 중 어떤 것을 원하나요?"],
    id: ["Apa yang mereka bawa ke hidup Anda yang sebelumnya tidak ada?", "Apa yang mereka lakukan yang membuat Anda merasa aman?", "Apa yang Anda harap mereka tidak pernah ragukan?", "Ingin yang playful, romantis, atau sangat tulus?"],
  },
  closure: {
    en: ["What do you want to put down gently?", "What truth do you want to name with care?", "What peace do you want for both of you?", "Do you want it forgiving, neutral, or firm?"],
    es: ["¿Qué quieres dejar ir con suavidad?", "¿Qué verdad quieres nombrar con cuidado?", "¿Qué paz deseas para ambos?", "¿Lo quieres con perdón, neutral o firme?"],
    fr: ["Que voulez-vous déposer en douceur ?", "Quelle vérité voulez-vous nommer avec soin ?", "Quelle paix souhaitez-vous pour vous deux ?", "Voulez-vous que ce soit pardonnant, neutre ou ferme ?"],
    pt: ["O que quer soltar com delicadeza?", "Que verdade quer nomear com cuidado?", "Que paz deseja para vocês dois?", "Quer algo com perdão, neutro ou firme?"],
    ar: ["ماذا تريد أن تتخلى عنه بلطف؟", "ما الحقيقة التي تريد تسميتها بعناية؟", "ما السلام الذي تريده لكليكما؟", "هل تريدها متسامحة، محايدة أم حازمة؟"],
    ja: ["そっと手放したいものは何ですか？", "丁寧に伝えたい真実は？", "二人にとってどんな平和を望みますか？", "許し、中立、毅然のどれがいいですか？"],
    ko: ["부드럽게 내려놓고 싶은 것은 무엇인가요?", "조심스럽게 말하고 싶은 진실은?", "둘 다를 위한 어떤 평화를 원하나요?", "용서하는, 중립적인, 단호한 중 어떤 것을 원하나요?"],
    id: ["Apa yang ingin Anda lepaskan dengan lembut?", "Kebenaran apa yang ingin Anda ungkapkan dengan hati-hati?", "Kedamaian apa yang Anda inginkan untuk kalian berdua?", "Ingin yang memaafkan, netral, atau tegas?"],
  },
  custom: {
    en: ["What kind of message is this? (Birthday / Congratulations / Thank You / Thinking of You / Something Else)", "What's one moment with them that always makes you smile?", "What do you hope this message makes them feel?", "Do you want it playful, sweet, or deeply emotional?"],
    es: ["¿Qué tipo de mensaje es? (Cumpleaños / Felicitaciones / Gracias / Pensando en Ti / Algo Más)", "¿Cuál es un momento con ellos que te hace sonreír?", "¿Qué esperas que este mensaje les haga sentir?", "¿Lo quieres juguetón, dulce o profundamente emocional?"],
    fr: ["Quel type de message est-ce ? (Anniversaire / Félicitations / Merci / Je pense à toi / Autre)", "Quel moment avec eux vous fait toujours sourire ?", "Que voulez-vous que ce message leur fasse ressentir ?", "Voulez-vous que ce soit ludique, doux ou profondément émotionnel ?"],
    pt: ["Que tipo de mensagem é esta? (Aniversário / Parabéns / Obrigado / Pensando em Você / Outro)", "Qual momento com eles sempre te faz sorrir?", "O que espera que esta mensagem os faça sentir?", "Quer algo divertido, doce ou profundamente emocional?"],
    ar: ["ما نوع هذه الرسالة؟ (عيد ميلاد / تهنئة / شكر / أفكر فيك / أخرى)", "ما اللحظة معهم التي تجعلك تبتسم دائماً؟", "ماذا تأمل أن تشعرهم هذه الرسالة؟", "هل تريدها مرحة، حلوة أم عاطفية بعمق؟"],
    ja: ["どんなメッセージですか？（誕生日 / おめでとう / ありがとう / あなたを思って / その他）", "一緒にいて必ず笑顔になる瞬間は？", "このメッセージでどう感じてほしいですか？", "遊び心、優しさ、深い感情のどれがいいですか？"],
    ko: ["어떤 종류의 메시지인가요? (생일 / 축하 / 감사 / 당신을 생각하며 / 기타)", "함께한 순간 중 항상 미소 짓게 하는 것은?", "이 메시지로 무엇을 느끼길 바라나요?", "장난스럽게, 달콤하게, 깊은 감정으로 중 어떤 것을 원하나요?"],
    id: ["Pesan jenis apa ini? (Ulang Tahun / Selamat / Terima Kasih / Memikirkan Anda / Lainnya)", "Momen apa bersama mereka yang selalu membuat Anda tersenyum?", "Apa yang Anda harap mereka rasakan dari pesan ini?", "Ingin yang playful, manis, atau sangat emosional?"],
  },
  birthday: {
    en: ["What do you want them to remember on their special day?", "What makes them one of a kind?", "What year or moment are you proud of them for?", "What blessing or wish do you want to speak over them?"],
    es: ["¿Qué quieres que recuerden en su día especial?", "¿Qué los hace únicos?", "¿De qué año o momento estás orgulloso/a de ellos?", "¿Qué bendición o deseo quieres expresarles?"],
    fr: ["Que voulez-vous qu'ils retiennent de leur journée spéciale ?", "Qu'est-ce qui les rend uniques ?", "De quelle année ou moment êtes-vous fier(e) pour eux ?", "Quelle bénédiction ou souhait voulez-vous leur adresser ?"],
    pt: ["O que quer que lembrem no seu dia especial?", "O que os torna únicos?", "De que ano ou momento você tem orgulho deles?", "Que bênção ou desejo quer expressar?"],
    ar: ["ما الذي تريدهم أن يتذكروه في يومهم المميز؟", "ما الذي يجعلهم فريدين؟", "أي سنة أو لحظة تفخر بهم فيها؟", "ما البركة أو الأمنية التي تريد أن تقولها لهم؟"],
    ja: ["特別な日に覚えていてほしいことは？", "彼らをかけがえのない存在にしているものは？", "どの年や瞬間を誇りに思いますか？", "どんな祝福や願いを伝えたいですか？"],
    ko: ["특별한 날에 기억해 주길 바라는 것은?", "그들을 특별하게 만드는 것은?", "어떤 해나 순간이 자랑스러운가요?", "어떤 축복이나 소원을 전하고 싶나요?"],
    id: ["Apa yang ingin mereka ingat di hari spesial mereka?", "Apa yang membuat mereka satu-satunya?", "Tahun atau momen apa yang Anda banggakan?", "Berkat atau harapan apa yang ingin Anda ucapkan?"],
  },
  anniversary: {
    en: ["What journey have you shared together?", "What has this relationship meant to you?", "What moments define your bond?", "What do you want to promise, honor, or remember?"],
    es: ["¿Qué camino han recorrido juntos?", "¿Qué ha significado esta relación para ti?", "¿Qué momentos definen su vínculo?", "¿Qué quieres prometer, honrar o recordar?"],
    fr: ["Quel chemin avez-vous parcouru ensemble ?", "Qu'a signifié cette relation pour vous ?", "Quels moments définissent votre lien ?", "Que voulez-vous promettre, honorer ou vous rappeler ?"],
    pt: ["Que jornada vocês compartilharam?", "O que essa relação significou para você?", "Que momentos definem o vínculo de vocês?", "O que quer prometer, honrar ou lembrar?"],
    ar: ["ما الرحلة التي شاركتماها معاً؟", "ماذا تعني هذه العلاقة لك؟", "ما اللحظات التي تحدد رابطتكما؟", "ماذا تريد أن تعد، تكرم، أو تتذكر؟"],
    ja: ["一緒に歩んできた旅路は？", "この関係はあなたにとって何を意味しますか？", "絆を定義する瞬間は？", "何を約束し、称え、覚えていたいですか？"],
    ko: ["함께 걸어온 여정은?", "이 관계가 당신에게 어떤 의미인가요?", "유대를 정의하는 순간은?", "무엇을 약속하고, 기리고, 기억하고 싶나요?"],
    id: ["Perjalanan apa yang telah kalian lalui bersama?", "Apa arti hubungan ini bagi Anda?", "Momen apa yang mendefinisikan ikatan kalian?", "Apa yang ingin Anda janjikan, hormati, atau kenang?"],
  },
  celebration: {
    en: ["What makes this person worth celebrating?", "What do they bring into your life?", "What moment with them makes you smile?", "What do you want them to feel when they read this?"],
    es: ["¿Qué hace que esta persona merezca ser celebrada?", "¿Qué aportan a tu vida?", "¿Qué momento con ellos te hace sonreír?", "¿Qué quieres que sientan al leer esto?"],
    fr: ["Qu'est-ce qui rend cette personne digne d'être célébrée ?", "Qu'apportent-ils dans votre vie ?", "Quel moment avec eux vous fait sourire ?", "Que voulez-vous qu'ils ressentent en lisant ceci ?"],
    pt: ["O que torna esta pessoa digna de ser celebrada?", "O que trazem para sua vida?", "Que momento com eles te faz sorrir?", "O que quer que sintam ao ler isso?"],
    ar: ["ما الذي يجعل هذا الشخص يستحق الاحتفاء؟", "ماذا يضيفون لحياتك؟", "ما اللحظة معهم التي تجعلك تبتسم؟", "ماذا تريدهم أن يشعروا عند قراءة هذا؟"],
    ja: ["この人を祝う価値があるのはなぜですか？", "あなたの人生に何をもたらしてくれますか？", "一緒にいて笑顔になる瞬間は？", "これを読んだ時にどう感じてほしいですか？"],
    ko: ["이 사람이 축하받을 만한 이유는?", "당신의 삶에 무엇을 가져다 주나요?", "함께한 어떤 순간이 미소 짓게 하나요?", "이것을 읽었을 때 어떤 감정을 느끼길 바라나요?"],
    id: ["Apa yang membuat orang ini layak dirayakan?", "Apa yang mereka bawa ke hidup Anda?", "Momen apa bersama mereka yang membuat Anda tersenyum?", "Apa yang Anda ingin mereka rasakan saat membaca ini?"],
  },
  encouragement: {
    en: ["What challenge are they facing right now?", "What strength have you seen in them that they might not see?", "What do you want them to believe about themselves?", "What energy do you want this to carry—gentle or bold?"],
    es: ["¿Qué desafío enfrentan ahora?", "¿Qué fortaleza has visto en ellos?", "¿Qué quieres que crean de sí mismos?", "¿Qué energía quieres que lleve: suave o audaz?"],
    fr: ["Quel défi traversent-ils en ce moment ?", "Quelle force avez-vous vue en eux ?", "Que voulez-vous qu'ils croient d'eux-mêmes ?", "Quelle énergie voulez-vous : douce ou audacieuse ?"],
    pt: ["Que desafio estão enfrentando agora?", "Que força você viu neles?", "O que quer que acreditem sobre si mesmos?", "Que energia quer: suave ou ousada?"],
    ar: ["ما التحدي الذي يواجهونه الآن؟", "ما القوة التي رأيتها فيهم؟", "ماذا تريدهم أن يصدقوا عن أنفسهم؟", "ما الطاقة التي تريدها: لطيفة أم جريئة؟"],
    ja: ["今どんな挑戦に直面していますか？", "彼らの中にどんな強さを見ましたか？", "自分自身について何を信じてほしいですか？", "優しい雰囲気、大胆な雰囲気のどちらがいいですか？"],
    ko: ["지금 어떤 도전에 직면해 있나요?", "그들에게서 어떤 강점을 보았나요?", "자신에 대해 무엇을 믿길 바라나요?", "부드러운 에너지, 대담한 에너지 중 어떤 것을 원하나요?"],
    id: ["Tantangan apa yang mereka hadapi saat ini?", "Kekuatan apa yang Anda lihat dalam diri mereka?", "Apa yang Anda ingin mereka percayai tentang diri mereka?", "Energi apa yang Anda inginkan: lembut atau berani?"],
  },
};

// Relationship-specific question overrides
type RelationshipKey = 'crush' | 'parent' | 'grandparent' | 'spouse' | 'friend' | 'child' | 'sibling' | 'mentor' | 'colleague' | 'other';
const RELATIONSHIP_QUESTIONS: Partial<Record<RelationshipKey, Record<string, string[]>>> = {
  crush: {
    en: ["What first caught your attention about them?", "What's something small they do that stays with you?", "What do you wish you could tell them in person?", "Do you want this to feel playful, sincere, or romantically honest?"],
    es: ["¿Qué fue lo primero que llamó tu atención de esa persona?", "¿Qué es algo pequeño que hacen y que se te queda grabado?", "¿Qué te gustaría poder decirles en persona?", "¿Quieres que se sienta juguetón, sincero o románticamente honesto?"],
    fr: ["Qu'est-ce qui a d'abord attiré votre attention chez cette personne ?", "Quel petit geste fait-elle qui reste gravé en vous ?", "Que souhaiteriez-vous pouvoir leur dire en personne ?", "Voulez-vous que ce soit ludique, sincère ou romantiquement honnête ?"],
    pt: ["O que primeiro chamou sua atenção nessa pessoa?", "Qual pequena coisa ela faz que fica com você?", "O que gostaria de poder dizer pessoalmente?", "Quer algo divertido, sincero ou romanticamente honesto?"],
    ar: ["ما أول شيء لفت انتباهك فيهم؟", "ما الشيء الصغير الذي يفعلونه ويبقى معك؟", "ماذا تتمنى لو تستطيع إخبارهم شخصياً؟", "هل تريدها مرحة، صادقة أم رومانسية بصدق؟"],
    ja: ["最初に惹かれたことは何ですか？", "彼らのどんな小さな仕草が心に残りますか？", "直接伝えられるとしたら何を言いたいですか？", "遊び心、誠実、ロマンチックに正直のどれがいいですか？"],
    ko: ["처음 끌린 것은 무엇인가요?", "그들의 어떤 작은 행동이 기억에 남나요?", "직접 말할 수 있다면 무엇을 전하고 싶나요?", "장난스럽게, 진심으로, 로맨틱하게 솔직하게 중 어떤 것을 원하나요?"],
    id: ["Apa yang pertama menarik perhatian Anda tentang mereka?", "Hal kecil apa yang mereka lakukan yang terus terkenang?", "Apa yang ingin Anda sampaikan langsung?", "Ingin yang playful, tulus, atau romantis jujur?"],
  },
  parent: {
    en: ["What's a lesson they taught you that shaped who you are?", "What moment with them do you treasure most?", "What do you wish they knew about how you see them?", "Do you want this to feel grateful, warm, or deeply emotional?"],
    es: ["¿Qué lección te enseñaron que moldeó quién eres?", "¿Qué momento con ellos atesoras más?", "¿Qué desearías que supieran sobre cómo los ves?", "¿Lo quieres agradecido, cálido o profundamente emocional?"],
    fr: ["Quelle leçon vous ont-ils apprise qui a façonné qui vous êtes ?", "Quel moment avec eux chérissez-vous le plus ?", "Que souhaiteriez-vous qu'ils sachent sur la façon dont vous les voyez ?", "Voulez-vous que ce soit reconnaissant, chaleureux ou profondément émotionnel ?"],
    pt: ["Que lição te ensinaram que moldou quem você é?", "Qual momento com eles você mais valoriza?", "O que gostaria que soubessem sobre como você os vê?", "Quer algo grato, caloroso ou profundamente emocional?"],
    ar: ["ما الدرس الذي علموك إياه وشكّل شخصيتك؟", "ما اللحظة معهم التي تعتز بها أكثر؟", "ماذا تتمنى لو يعرفون عن كيف تراهم؟", "هل تريدها ممتنة، دافئة أم عاطفية بعمق؟"],
    ja: ["あなたを形作った、彼らから学んだ教訓は？", "彼らとの一番大切な瞬間は？", "自分がどう見ているか知ってほしいことは？", "感謝、温かさ、深い感情のどれがいいですか？"],
    ko: ["당신을 만든 그들의 가르침은 무엇인가요?", "가장 소중히 여기는 순간은?", "당신이 어떻게 보는지 알았으면 하는 것은?", "감사하게, 따뜻하게, 깊은 감정으로 중 어떤 것을 원하나요?"],
    id: ["Pelajaran apa yang mereka ajarkan yang membentuk Anda?", "Momen apa bersama mereka yang paling Anda hargai?", "Apa yang Anda harap mereka tahu tentang cara Anda melihat mereka?", "Ingin yang penuh syukur, hangat, atau sangat emosional?"],
  },
  spouse: {
    en: ["What's something they do that makes you fall for them again?", "What moment together defines your relationship?", "What do you want them to never forget about how you feel?", "Do you want this to feel romantic, comforting, or deeply intimate?"],
    es: ["¿Qué es algo que hacen que te enamora de nuevo?", "¿Qué momento juntos define su relación?", "¿Qué quieres que nunca olviden sobre cómo te sientes?", "¿Lo quieres romántico, reconfortante o profundamente íntimo?"],
    fr: ["Que font-ils qui vous fait retomber amoureux ?", "Quel moment ensemble définit votre relation ?", "Que voulez-vous qu'ils n'oublient jamais sur vos sentiments ?", "Voulez-vous que ce soit romantique, réconfortant ou profondément intime ?"],
    pt: ["O que fazem que te faz se apaixonar de novo?", "Que momento juntos define a relação?", "O que quer que nunca esqueçam sobre como você se sente?", "Quer algo romântico, reconfortante ou profundamente íntimo?"],
    ar: ["ما الشيء الذي يفعلونه ويجعلك تقع في حبهم مجدداً؟", "ما اللحظة التي تعرّف علاقتكما؟", "ما الذي تريدهم ألا ينسوه عن مشاعرك؟", "هل تريدها رومانسية، مريحة أم حميمية بعمق؟"],
    ja: ["また惚れ直してしまう、彼らの行動は？", "二人の関係を定義する瞬間は？", "あなたの気持ちについて絶対に忘れてほしくないことは？", "ロマンチック、安心感、深い親密さのどれがいいですか？"],
    ko: ["다시 반하게 만드는 그들의 행동은?", "함께한 순간 중 관계를 정의하는 것은?", "당신의 감정에 대해 절대 잊지 않았으면 하는 것은?", "로맨틱하게, 편안하게, 깊이 친밀하게 중 어떤 것을 원하나요?"],
    id: ["Apa yang mereka lakukan yang membuat Anda jatuh cinta lagi?", "Momen bersama apa yang mendefinisikan hubungan Anda?", "Apa yang Anda ingin mereka tidak pernah lupakan tentang perasaan Anda?", "Ingin yang romantis, menenangkan, atau sangat intim?"],
  },
  friend: {
    en: ["What's a time they showed up for you when it really mattered?", "What inside joke or shared memory captures your friendship?", "What do they bring to your life that no one else does?", "Do you want this to feel heartfelt, fun, or nostalgic?"],
    es: ["¿Cuándo estuvieron ahí para ti cuando realmente importaba?", "¿Qué broma interna o recuerdo compartido captura su amistad?", "¿Qué aportan a tu vida que nadie más aporta?", "¿Lo quieres sincero, divertido o nostálgico?"],
    fr: ["Quand se sont-ils montrés présents quand ça comptait vraiment ?", "Quelle blague privée ou souvenir commun capture votre amitié ?", "Qu'apportent-ils dans votre vie que personne d'autre n'apporte ?", "Voulez-vous que ce soit sincère, amusant ou nostalgique ?"],
    pt: ["Quando estiveram lá quando realmente importava?", "Que piada interna ou memória compartilhada captura a amizade?", "O que trazem para sua vida que ninguém mais traz?", "Quer algo sincero, divertido ou nostálgico?"],
    ar: ["متى كانوا بجانبك عندما كان الأمر مهماً فعلاً؟", "ما النكتة الداخلية أو الذكرى المشتركة التي تجسد صداقتكم؟", "ماذا يضيفون لحياتك ولا يضيفه أحد غيرهم؟", "هل تريدها صادقة، مرحة أم حنينية؟"],
    ja: ["本当に大事な時に来てくれた瞬間は？", "友情を象徴する内輪ネタや共有の思い出は？", "他の誰にもない、彼らが人生にもたらすものは？", "心からの、楽しい、懐かしいのどれがいいですか？"],
    ko: ["정말 중요할 때 함께해 준 순간은?", "우정을 담은 내부 농담이나 공유 추억은?", "다른 누구도 가져다 주지 못하는 것은?", "진심으로, 재미있게, 향수를 느끼며 중 어떤 것을 원하나요?"],
    id: ["Kapan mereka ada untuk Anda saat benar-benar penting?", "Lelucon internal atau kenangan bersama apa yang menggambarkan persahabatan?", "Apa yang mereka bawa ke hidup Anda yang tidak dimiliki orang lain?", "Ingin yang tulus, menyenangkan, atau nostalgia?"],
  },
  grandparent: {
    en: ["What lesson did they teach you without even trying?", "What tradition or habit reminds you of them?", "What part of you carries their influence?", "Do you want this to feel nostalgic, grateful, or tender?"],
    es: ["¿Qué lección te enseñaron sin siquiera intentarlo?", "¿Qué tradición o costumbre te recuerda a ellos?", "¿Qué parte de ti lleva su influencia?", "¿Lo quieres nostálgico, agradecido o tierno?"],
    fr: ["Quelle leçon vous ont-ils enseignée sans même essayer ?", "Quelle tradition ou habitude vous rappelle d'eux ?", "Quelle partie de vous porte leur influence ?", "Voulez-vous que ce soit nostalgique, reconnaissant ou tendre ?"],
    pt: ["Que lição ensinaram sem nem tentar?", "Que tradição ou hábito lembra deles?", "Que parte de você carrega a influência deles?", "Quer algo nostálgico, grato ou terno?"],
    ar: ["ما الدرس الذي علموك إياه دون أن يحاولوا؟", "ما التقليد أو العادة التي تذكرك بهم؟", "ما الجزء منك الذي يحمل تأثيرهم؟", "هل تريدها حنينية، ممتنة أم رقيقة؟"],
    ja: ["意図せず教えてくれた教訓は？", "彼らを思い出す伝統や習慣は？", "あなたのどの部分に彼らの影響がありますか？", "懐かしさ、感謝、優しさのどれがいいですか？"],
    ko: ["의도하지 않고 가르쳐 준 교훈은?", "그들을 떠올리게 하는 전통이나 습관은?", "당신의 어떤 부분에 그들의 영향이 있나요?", "향수, 감사, 부드러움 중 어떤 것을 원하나요?"],
    id: ["Pelajaran apa yang mereka ajarkan tanpa disengaja?", "Tradisi atau kebiasaan apa yang mengingatkan Anda pada mereka?", "Bagian mana dari Anda yang membawa pengaruh mereka?", "Ingin yang nostalgia, penuh syukur, atau lembut?"],
  },
  child: {
    en: ["What moment with them fills you with the most pride?", "What quality of theirs do you hope they never lose?", "What do you want them to know about how you see them?", "Do you want this to feel encouraging, protective, or deeply loving?"],
    es: ["¿Qué momento con ellos te llena de más orgullo?", "¿Qué cualidad de ellos esperas que nunca pierdan?", "¿Qué quieres que sepan sobre cómo los ves?", "¿Lo quieres alentador, protector o profundamente amoroso?"],
    fr: ["Quel moment avec eux vous remplit de fierté ?", "Quelle qualité espérez-vous qu'ils ne perdent jamais ?", "Que voulez-vous qu'ils sachent sur la façon dont vous les voyez ?", "Voulez-vous que ce soit encourageant, protecteur ou profondément aimant ?"],
    pt: ["Que momento com eles te enche de orgulho?", "Que qualidade espera que nunca percam?", "O que quer que saibam sobre como você os vê?", "Quer algo encorajador, protetor ou profundamente amoroso?"],
    ar: ["ما اللحظة معهم التي تملأك بالفخر؟", "ما الصفة التي تأمل ألا يفقدوها أبداً؟", "ماذا تريدهم أن يعرفوا عن كيف تراهم؟", "هل تريدها مشجعة، حامية أم محبة بعمق؟"],
    ja: ["彼らとの一番誇りに思う瞬間は？", "決して失ってほしくない彼らの資質は？", "自分がどう見ているか知ってほしいことは？", "励まし、守り、深い愛のどれがいいですか？"],
    ko: ["가장 자랑스러운 순간은?", "절대 잃지 않았으면 하는 자질은?", "당신이 어떻게 보는지 알았으면 하는 것은?", "격려, 보호, 깊은 사랑 중 어떤 것을 원하나요?"],
    id: ["Momen apa bersama mereka yang paling membuat Anda bangga?", "Kualitas apa yang Anda harap mereka tidak pernah hilangkan?", "Apa yang Anda ingin mereka tahu tentang cara Anda melihat mereka?", "Ingin yang menyemangati, melindungi, atau sangat penuh cinta?"],
  },
};

const LENGTH_OPTIONS: { value: LengthOption; labels: Record<string, string>; range: string; legacyOnly?: boolean }[] = [
  { value: 'short', labels: { en: 'Short', es: 'Corto', fr: 'Court', pt: 'Curto', ar: 'قصير', ja: '短い', ko: '짧게', id: 'Pendek' }, range: '120–220' },
  { value: 'standard', labels: { en: 'Standard', es: 'Estándar', fr: 'Standard', pt: 'Padrão', ar: 'قياسي', ja: '標準', ko: '표준', id: 'Standar' }, range: '280–450' },
  { value: 'long', labels: { en: 'Long', es: 'Largo', fr: 'Long', pt: 'Longo', ar: 'طويل', ja: '長い', ko: '길게', id: 'Panjang' }, range: '600–900' },
  { value: 'keepsake', labels: { en: 'Keepsake', es: 'Recuerdo', fr: 'Souvenir', pt: 'Lembrança', ar: 'تذكار', ja: '記念', ko: '기념', id: 'Kenang-kenangan' }, range: '900–1600', legacyOnly: true },
];

const REFINE_OPTIONS: Record<string, string[]> = {
  en: ['Shorter', 'More heartfelt', 'More direct', 'Add a memory', 'Keep my voice'],
  es: ['Más corto', 'Más sincero', 'Más directo', 'Agrega un recuerdo', 'Mantén mi voz'],
  fr: ['Plus court', 'Plus sincère', 'Plus direct', 'Ajouter un souvenir', 'Garder ma voix'],
  pt: ['Mais curto', 'Mais sincero', 'Mais direto', 'Adicionar memória', 'Manter minha voz'],
  ar: ['أقصر', 'أكثر صدقاً', 'أكثر مباشرة', 'أضف ذكرى', 'حافظ على صوتي'],
  ja: ['短く', 'より心を込めて', 'より直接的に', '思い出を追加', '自分の声を保つ'],
  ko: ['더 짧게', '더 진심으로', '더 직접적으로', '추억 추가', '내 목소리 유지'],
  id: ['Lebih pendek', 'Lebih tulus', 'Lebih langsung', 'Tambah kenangan', 'Pertahankan gaya saya'],
};

const USE_DRAFT_LABEL: Record<string, string> = {
  en: 'Use this draft',
  es: 'Usar este borrador',
  fr: 'Utiliser ce brouillon',
  pt: 'Usar este rascunho',
  ar: 'استخدام هذه المسودة',
  ja: 'この下書きを使用',
  ko: '이 초안 사용',
  id: 'Gunakan draf ini',
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export function JacKeyGuideV2({ formData, updateFormData, onDismiss }: JacKeyGuideV2Props) {
  const { language } = useLanguage();
  const lang = language;
  const { credits, canUseAiAssist, useAiAssist } = useUserCredits();

  const bundleType = credits?.highest_bundle_purchased;
  const isStarter = !bundleType;
  const isConnection = bundleType === 'connection' || bundleType === 'legacy';
  const isLegacy = bundleType === 'legacy';

  const [step, setStep] = useState<FlowStep>('intro');
  const [mode, setMode] = useState<JacKeyMode>('quick');
  const [lengthOption, setLengthOption] = useState<LengthOption>('standard');
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [drafts, setDrafts] = useState<string[]>([]);
  const [selectedDraft, setSelectedDraft] = useState(0);
  const [refineCount, setRefineCount] = useState(0);
  const [dismissCount, setDismissCount] = useState(0);

  // Detect rewrite mode: user has >50 chars of meaningful text
  const userHasText = formData.message.length > 50;

  // Resolve intent from template + relationship
  const intentSlug = resolveIntentSlug(formData.template, formData.relationship);
  const intentConfig = INTENT_DATA[intentSlug];

  // Get questions: use intent-specific questions (3 per intent)
  const questions = lstrArr(intentConfig.questions, lang);

  // Get contextual intro line from intent data
  const introLine = lstr(intentConfig.intro, lang);
  const maxQuestions = mode === 'quick' ? 2 : mode === 'deep' ? Math.min(questions.length, 6) : 0;

  // Sentence starters for the UI
  const sentenceStarters = lstrArr(intentConfig.sentenceStarters, lang);
  // Unlock prompt for inactivity
  const unlockPromptText = lstr(intentConfig.unlockPrompt, lang);

  // "Do not annoy" rules
  useEffect(() => {
    const dismissed = localStorage.getItem('jackey_dismiss_count');
    const lastDismissed = localStorage.getItem('jackey_last_dismissed');
    if (dismissed) setDismissCount(parseInt(dismissed));

    // If dismissed twice within 7 days, stay collapsed
    if (dismissed && parseInt(dismissed) >= 2 && lastDismissed) {
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      if (parseInt(lastDismissed) > sevenDaysAgo) {
        setStep('collapsed');
      }
    }
  }, []);

  // Auto-collapse if user typed >250 chars continuously
  useEffect(() => {
    if (formData.message.length > 250 && step === 'intro') {
      setStep('collapsed');
    }
  }, [formData.message.length, step]);

  const toastCopy = {
    en: { dismiss: "I'll stay quietly in the background.", wait: 'Please wait a moment before trying again', limitReached: 'AI assist limit reached.', error: 'Something went wrong. Please try again.', inserted: 'Draft inserted!', upgradeRefine: 'Upgrade to Connection for more refine options.', refineFailed: 'Refine failed. Try again.', deepUnlockOnly: 'Deep Unlock is available with Connection plan.' },
    es: { dismiss: "Me quedaré tranquilo en segundo plano.", wait: 'Espera un momento antes de intentar de nuevo', limitReached: 'Límite de asistencia IA alcanzado.', error: 'Algo salió mal. Inténtalo de nuevo.', inserted: '¡Borrador insertado!', upgradeRefine: 'Actualiza a Connection para más opciones de refinamiento.', refineFailed: 'Falló el refinamiento. Inténtalo de nuevo.', deepUnlockOnly: 'Desbloqueo Profundo disponible con el plan Connection.' },
    fr: { dismiss: "Je resterai tranquille en arrière-plan.", wait: 'Veuillez patienter un instant', limitReached: "Limite d'assistance IA atteinte.", error: 'Une erreur est survenue. Réessayez.', inserted: 'Brouillon inséré !', upgradeRefine: 'Passez à Connection pour plus de raffinement.', refineFailed: 'Échec du raffinement. Réessayez.', deepUnlockOnly: 'Déblocage Profond disponible avec le plan Connection.' },
    pt: { dismiss: "Ficarei quieto em segundo plano.", wait: 'Aguarde um momento antes de tentar novamente', limitReached: 'Limite de assistência IA atingido.', error: 'Algo deu errado. Tente novamente.', inserted: 'Rascunho inserido!', upgradeRefine: 'Atualize para Connection para mais opções de refinamento.', refineFailed: 'Falha no refinamento. Tente novamente.', deepUnlockOnly: 'Desbloqueio Profundo disponível com o plano Connection.' },
    ar: { dismiss: "سأبقى هادئاً في الخلفية.", wait: 'يرجى الانتظار لحظة قبل المحاولة مرة أخرى', limitReached: 'تم الوصول إلى حد مساعدة الذكاء الاصطناعي.', error: 'حدث خطأ. حاول مرة أخرى.', inserted: 'تم إدراج المسودة!', upgradeRefine: 'قم بالترقية إلى Connection لمزيد من خيارات التحسين.', refineFailed: 'فشل التحسين. حاول مرة أخرى.', deepUnlockOnly: 'الفتح العميق متاح مع خطة Connection.' },
    ja: { dismiss: "静かに背景にいます。", wait: 'しばらくしてからもう一度お試しください', limitReached: 'AIアシストの上限に達しました。', error: 'エラーが発生しました。もう一度お試しください。', inserted: '下書きが挿入されました！', upgradeRefine: 'Connectionプランで追加の調整オプション。', refineFailed: '調整に失敗しました。もう一度お試しください。', deepUnlockOnly: 'ディープアンロックはConnectionプランで利用可能です。' },
    ko: { dismiss: "조용히 배경에 있을게요.", wait: '잠시 후 다시 시도해 주세요', limitReached: 'AI 지원 한도에 도달했습니다.', error: '오류가 발생했습니다. 다시 시도하세요.', inserted: '초안이 삽입되었습니다!', upgradeRefine: 'Connection으로 업그레이드하여 더 많은 조정 옵션을 사용하세요.', refineFailed: '조정에 실패했습니다. 다시 시도하세요.', deepUnlockOnly: '딥 언락은 Connection 플랜에서 사용 가능합니다.' },
    id: { dismiss: "Saya akan tetap diam di latar belakang.", wait: 'Tunggu sebentar sebelum mencoba lagi', limitReached: 'Batas bantuan AI tercapai.', error: 'Terjadi kesalahan. Coba lagi.', inserted: 'Draf disisipkan!', upgradeRefine: 'Tingkatkan ke Connection untuk opsi penyempurnaan lebih.', refineFailed: 'Penyempurnaan gagal. Coba lagi.', deepUnlockOnly: 'Deep Unlock tersedia dengan paket Connection.' },
  };
  const tc = lc(toastCopy, lang);

  const handleDismiss = () => {
    const newCount = dismissCount + 1;
    setDismissCount(newCount);
    localStorage.setItem('jackey_dismiss_count', newCount.toString());
    localStorage.setItem('jackey_last_dismissed', Date.now().toString());
    toast.info(tc.dismiss, { duration: 2000 });
    onDismiss();
  };

  const handleAnswerSubmit = () => {
    if (!currentAnswer.trim()) return;
    const newAnswers = [...answers, currentAnswer.trim()];
    setAnswers(newAnswers);
    setCurrentAnswer('');

    if (currentQuestionIdx + 1 < maxQuestions) {
      setCurrentQuestionIdx(prev => prev + 1);
    } else {
      handleGenerate(newAnswers);
    }
  };

  const handleGenerate = useCallback(async (finalAnswers?: string[]) => {
    setStep('generating');
    setIsGenerating(true);
    await useAiAssist();

    const answersToSend = finalAnswers || answers;
    const questionsUsed = questions.slice(0, answersToSend.length);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/announcement-ai-assist`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type: 'jackey_v2_generate',
          recipientName: formData.recipientName,
          relationship: formData.relationship,
          template: formData.template,
          mode,
          lengthOption,
          questions: questionsUsed,
          answers: answersToSend,
          existingMessage: mode === 'rewrite' ? formData.message : undefined,
          draftCount: isConnection ? 2 : 1,
        }),
      });

      if (!response.ok) {
        const statusCode = response.status;
        console.error('[JacKey] Generation failed with status:', statusCode);
        if (statusCode === 429) { toast.error(tc.wait); setStep('intro'); return; }
        if (statusCode === 402) { toast.info(tc.limitReached); setStep('complete'); return; }
        if (statusCode === 401) {
          // Token was rejected — retry once with a fresh session
          console.warn('[JacKey] 401 received, retrying with fresh session...');
          const retryHeaders = await getAuthHeaders();
          const retryResponse = await fetch(`${SUPABASE_URL}/functions/v1/announcement-ai-assist`, {
            method: 'POST',
            headers: retryHeaders,
            body: JSON.stringify({
              type: 'jackey_v2_generate',
              recipientName: formData.recipientName,
              relationship: formData.relationship,
              template: formData.template,
              mode,
              lengthOption,
              questions: questionsUsed,
              answers: answersToSend,
              existingMessage: mode === 'rewrite' ? formData.message : undefined,
              draftCount: isConnection ? 2 : 1,
            }),
          });
          if (!retryResponse.ok) throw new Error(`Retry failed: ${retryResponse.status}`);
          const retryData = await retryResponse.json();
          const retryDrafts = retryData.drafts && Array.isArray(retryData.drafts) && retryData.drafts.length > 0
            ? retryData.drafts
            : retryData.message ? [retryData.message] : null;
          if (!retryDrafts) throw new Error('No drafts in retry response');
          setDrafts(retryDrafts);
          setStep('drafts');
          return;
        }
        throw new Error(`Failed to generate (${statusCode})`);
      }

      const data = await response.json();
      const parsedDrafts = data.drafts && Array.isArray(data.drafts) && data.drafts.length > 0
        ? data.drafts
        : data.message ? [data.message] : null;

      if (!parsedDrafts || parsedDrafts.every((d: string) => !d.trim())) {
        console.error('[JacKey] Empty drafts received:', data);
        throw new Error('Empty response from AI');
      }

      setDrafts(parsedDrafts);
      setStep('drafts');
    } catch (error) {
      console.error('[JacKey] generation error:', error);
      toast.error(tc.error);
      setStep('intro');
    } finally {
      setIsGenerating(false);
    }
  }, [formData, mode, lengthOption, answers, questions, isConnection, lang, useAiAssist, tc]);

  const handleInsertDraft = (insertIdx: number) => {
    const draft = drafts[selectedDraft];
    if (!draft) return;

    if (insertIdx === 0) {
      updateFormData({ message: draft });
    } else if (insertIdx === 1) {
      updateFormData({ message: draft + (formData.message ? '\n\n' + formData.message : '') });
    } else {
      updateFormData({ message: (formData.message ? formData.message + '\n\n' : '') + draft });
    }
    toast.success(tc.inserted);
    setStep('complete');
  };

  const handleRefine = async (refineType: string) => {
    if (isStarter && refineCount >= 1) {
      toast.info(tc.upgradeRefine);
      return;
    }
    setIsGenerating(true);
    try {
      const refineHeaders = await getAuthHeaders();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/announcement-ai-assist`, {
        method: 'POST',
        headers: refineHeaders,
        body: JSON.stringify({
          type: 'jackey_v2_refine',
          recipientName: formData.recipientName,
          relationship: formData.relationship,
          template: formData.template,
          currentDraft: drafts[selectedDraft],
          refineType,
          lengthOption,
        }),
      });

      if (!response.ok) throw new Error('Failed to refine');
      const data = await response.json();
      if (data.message) {
        const newDrafts = [...drafts];
        newDrafts[selectedDraft] = data.message;
        setDrafts(newDrafts);
        setRefineCount(prev => prev + 1);
      }
    } catch (error) {
      toast.error(tc.refineFailed);
    } finally {
      setIsGenerating(false);
    }
  };

  const copy = {
    en: {
      collapsed: "Need help unlocking the right words?",
      helpMe: "I'd like some help",
      writeOwn: "I'll write on my own",
      quickMode: "Quick Unlock",
      quickDesc: "2 questions, 1 draft",
      deepMode: "Deep Unlock",
      deepDesc: "4–6 questions, 2 drafts, full refine",
      rewriteMode: "Rewrite & Polish",
      rewriteDesc: "Refine what you already wrote",
      length: "Length",
      words: "words",
      keepGoing: "Keep going",
      skip: "Skip",
      goBack: "Go back",
      generating: "Shaping your words...",
      warmSimple: "Warm & simple",
      deepReflective: "Deep & reflective",
      insertHow: "How would you like to use this?",
      refine: "Refine",
      starterLimit: "Starter: 1 refine",
      editNote: "This is just a starting place. Feel free to change anything so it sounds exactly like you.",
      connectionOnly: "Connection plan",
      legacyOnly: "Legacy plan",
      questionOf: "Question",
      of: "of",
      placeholder: "Take your time...",
    },
    es: {
      collapsed: "¿Necesitas ayuda para encontrar las palabras correctas?",
      helpMe: "Me gustaría ayuda",
      writeOwn: "Escribiré por mi cuenta",
      quickMode: "Desbloqueo Rápido",
      quickDesc: "2 preguntas, 1 borrador",
      deepMode: "Desbloqueo Profundo",
      deepDesc: "4–6 preguntas, 2 borradores, refinamiento completo",
      rewriteMode: "Reescribir y Pulir",
      rewriteDesc: "Refina lo que ya escribiste",
      length: "Longitud",
      words: "palabras",
      keepGoing: "Continuar",
      skip: "Omitir",
      goBack: "Volver",
      generating: "Dando forma a tus palabras...",
      warmSimple: "Cálido y simple",
      deepReflective: "Profundo y reflexivo",
      insertHow: "¿Cómo te gustaría usar esto?",
      refine: "Refinar",
      starterLimit: "Starter: 1 refinamiento",
      editNote: "Esto es solo un punto de partida. Siéntete libre de cambiar cualquier cosa para que suene exactamente como tú.",
      connectionOnly: "Plan Connection",
      legacyOnly: "Plan Legacy",
      questionOf: "Pregunta",
      of: "de",
      placeholder: "Tómate tu tiempo...",
    },
    fr: {
      collapsed: "Besoin d'aide pour trouver les bons mots ?",
      helpMe: "J'aimerais de l'aide",
      writeOwn: "J'écrirai seul(e)",
      quickMode: "Déblocage Rapide",
      quickDesc: "2 questions, 1 brouillon",
      deepMode: "Déblocage Profond",
      deepDesc: "4–6 questions, 2 brouillons, raffinement complet",
      rewriteMode: "Réécrire et Polir",
      rewriteDesc: "Affinez ce que vous avez déjà écrit",
      length: "Longueur",
      words: "mots",
      keepGoing: "Continuer",
      skip: "Passer",
      goBack: "Retour",
      generating: "Mise en forme de vos mots...",
      warmSimple: "Chaleureux et simple",
      deepReflective: "Profond et réfléchi",
      insertHow: "Comment souhaitez-vous l'utiliser ?",
      refine: "Affiner",
      starterLimit: "Starter : 1 affinage",
      editNote: "Ceci n'est qu'un point de départ. N'hésitez pas à tout modifier.",
      connectionOnly: "Plan Connection",
      legacyOnly: "Plan Legacy",
      questionOf: "Question",
      of: "sur",
      placeholder: "Prenez votre temps...",
    },
    pt: {
      collapsed: "Precisa de ajuda para encontrar as palavras certas?",
      helpMe: "Gostaria de ajuda",
      writeOwn: "Vou escrever sozinho(a)",
      quickMode: "Desbloqueio Rápido",
      quickDesc: "2 perguntas, 1 rascunho",
      deepMode: "Desbloqueio Profundo",
      deepDesc: "4–6 perguntas, 2 rascunhos, refinamento completo",
      rewriteMode: "Reescrever e Polir",
      rewriteDesc: "Refine o que você já escreveu",
      length: "Comprimento",
      words: "palavras",
      keepGoing: "Continuar",
      skip: "Pular",
      goBack: "Voltar",
      generating: "Dando forma às suas palavras...",
      warmSimple: "Caloroso e simples",
      deepReflective: "Profundo e reflexivo",
      insertHow: "Como gostaria de usar isso?",
      refine: "Refinar",
      starterLimit: "Starter: 1 refinamento",
      editNote: "Isso é só um ponto de partida. Sinta-se livre para mudar o que quiser.",
      connectionOnly: "Plano Connection",
      legacyOnly: "Plano Legacy",
      questionOf: "Pergunta",
      of: "de",
      placeholder: "Sem pressa...",
    },
    ar: {
      collapsed: "هل تحتاج مساعدة في إيجاد الكلمات المناسبة؟",
      helpMe: "أريد بعض المساعدة",
      writeOwn: "سأكتب بنفسي",
      quickMode: "فتح سريع",
      quickDesc: "سؤالان، مسودة واحدة",
      deepMode: "فتح عميق",
      deepDesc: "4–6 أسئلة، مسودتان، تحسين كامل",
      rewriteMode: "إعادة كتابة وتحسين",
      rewriteDesc: "حسّن ما كتبته بالفعل",
      length: "الطول",
      words: "كلمات",
      keepGoing: "استمر",
      skip: "تخطي",
      goBack: "رجوع",
      generating: "جارٍ صياغة كلماتك...",
      warmSimple: "دافئ وبسيط",
      deepReflective: "عميق وتأملي",
      insertHow: "كيف تريد استخدام هذا؟",
      refine: "تحسين",
      starterLimit: "Starter: تحسين واحد",
      editNote: "هذه مجرد نقطة بداية. لا تتردد في تغيير أي شيء ليعبر عنك.",
      connectionOnly: "خطة Connection",
      legacyOnly: "خطة Legacy",
      questionOf: "السؤال",
      of: "من",
      placeholder: "خذ وقتك...",
    },
    ja: {
      collapsed: "言葉を見つけるお手伝いが必要ですか？",
      helpMe: "手伝ってほしい",
      writeOwn: "自分で書きます",
      quickMode: "クイックアンロック",
      quickDesc: "質問2つ、下書き1つ",
      deepMode: "ディープアンロック",
      deepDesc: "質問4–6つ、下書き2つ、完全な調整",
      rewriteMode: "書き直して磨く",
      rewriteDesc: "すでに書いた内容を磨く",
      length: "長さ",
      words: "文字",
      keepGoing: "続ける",
      skip: "スキップ",
      goBack: "戻る",
      generating: "言葉を形にしています...",
      warmSimple: "温かくシンプル",
      deepReflective: "深く内省的",
      insertHow: "どのように使いますか？",
      refine: "調整",
      starterLimit: "Starter: 調整1回",
      editNote: "これは出発点です。あなたらしく自由に変更してください。",
      connectionOnly: "Connectionプラン",
      legacyOnly: "Legacyプラン",
      questionOf: "質問",
      of: "/",
      placeholder: "ゆっくりどうぞ...",
    },
    ko: {
      collapsed: "적절한 말을 찾는 데 도움이 필요하세요?",
      helpMe: "도움을 받고 싶어요",
      writeOwn: "직접 쓸게요",
      quickMode: "빠른 언락",
      quickDesc: "질문 2개, 초안 1개",
      deepMode: "딥 언락",
      deepDesc: "질문 4–6개, 초안 2개, 전체 조정",
      rewriteMode: "다시 쓰고 다듬기",
      rewriteDesc: "이미 작성한 내용을 다듬기",
      length: "길이",
      words: "단어",
      keepGoing: "계속하기",
      skip: "건너뛰기",
      goBack: "뒤로",
      generating: "말을 다듬고 있습니다...",
      warmSimple: "따뜻하고 간결하게",
      deepReflective: "깊고 성찰적으로",
      insertHow: "어떻게 사용하시겠어요?",
      refine: "조정",
      starterLimit: "Starter: 조정 1회",
      editNote: "이것은 시작점일 뿐입니다. 자유롭게 수정하세요.",
      connectionOnly: "Connection 플랜",
      legacyOnly: "Legacy 플랜",
      questionOf: "질문",
      of: "/",
      placeholder: "천천히 하세요...",
    },
    id: {
      collapsed: "Butuh bantuan menemukan kata-kata yang tepat?",
      helpMe: "Saya ingin bantuan",
      writeOwn: "Saya akan menulis sendiri",
      quickMode: "Buka Cepat",
      quickDesc: "2 pertanyaan, 1 draf",
      deepMode: "Buka Mendalam",
      deepDesc: "4–6 pertanyaan, 2 draf, penyempurnaan penuh",
      rewriteMode: "Tulis Ulang & Poles",
      rewriteDesc: "Sempurnakan yang sudah Anda tulis",
      length: "Panjang",
      words: "kata",
      keepGoing: "Lanjutkan",
      skip: "Lewati",
      goBack: "Kembali",
      generating: "Membentuk kata-kata Anda...",
      warmSimple: "Hangat & sederhana",
      deepReflective: "Dalam & reflektif",
      insertHow: "Bagaimana Anda ingin menggunakan ini?",
      refine: "Sempurnakan",
      starterLimit: "Starter: 1 penyempurnaan",
      editNote: "Ini hanya titik awal. Silakan ubah agar terdengar seperti Anda.",
      connectionOnly: "Paket Connection",
      legacyOnly: "Paket Legacy",
      questionOf: "Pertanyaan",
      of: "dari",
      placeholder: "Pelan-pelan saja...",
    },
  
    hi: {
      collapsed: "Need help unlocking the right words?",
      helpMe: "I'd like some help",
      writeOwn: "I'll write on my own",
      quickMode: "Quick Unlock",
      quickDesc: "2 questions, 1 draft",
      deepMode: "Deep Unlock",
      deepDesc: "4–6 questions, 2 drafts, full refine",
      rewriteMode: "Rewrite & Polish",
      rewriteDesc: "Refine what you already wrote",
      length: "Length",
      words: "words",
      keepGoing: "Keep going",
      skip: "Skip",
      goBack: "Go back",
      generating: "Shaping your words...",
      warmSimple: "Warm & simple",
      deepReflective: "Deep & reflective",
      insertHow: "How would you like to use this?",
      refine: "Refine",
      starterLimit: "Starter: 1 refine",
      editNote: "This is just a starting place. Feel free to change anything so it sounds exactly like you.",
      connectionOnly: "Connection plan",
      legacyOnly: "Legacy plan",
      questionOf: "Question",
      of: "of",
      placeholder: "Take your time...",
    },
};
  const c = lc(copy, lang);

  // Collapsed state
  if (step === 'collapsed' || step === 'complete') {
    return (
      <div className="flex justify-center mb-4">
        <button
          type="button"
          onClick={() => setStep('intro')}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(160,35%,55%)/0.08] border border-[hsl(160,35%,55%)/0.2] hover:bg-[hsl(160,35%,55%)/0.15] transition-colors text-sm text-muted-foreground"
        >
          <JacKeyIcon size="sm" />
          <span>{c.collapsed}</span>
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
      className="bg-gradient-to-br from-[hsl(160,35%,95%)] via-[hsl(160,30%,97%)] to-[hsl(160,25%,96%)] dark:from-[hsl(160,25%,15%)] dark:via-[hsl(160,20%,12%)] dark:to-[hsl(160,20%,10%)] rounded-xl p-5 border border-[hsl(160,35%,80%)] dark:border-[hsl(160,25%,25%)] mb-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <JacKeyIcon size="md" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground">JacKey</h3>
            {intentSlug !== 'custom' && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[hsl(160,35%,45%)/0.12] text-[hsl(160,35%,35%)] dark:text-[hsl(160,35%,65%)]">
                {intentSlug.replace('_', ' ').replace('secret admire', 'crush').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground italic">{lstr(TEMPLATE_INTROS[formData.template as TemplateType] ?? TEMPLATE_INTROS.custom!, lang) || introLine}</p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {/* INTRO */}
        {step === 'intro' && (
          <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" onClick={() => setStep('mode_select')} className="flex-1 gap-2 bg-[hsl(160,35%,45%)] hover:bg-[hsl(160,35%,40%)] text-white">
                {c.helpMe}
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" onClick={handleDismiss} className="flex-1">
                {c.writeOwn}
              </Button>
            </div>
          </motion.div>
        )}

        {/* MODE SELECT */}
        {step === 'mode_select' && (
          <motion.div key="mode_select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="space-y-2">
              {/* Quick Unlock - always available */}
              <button type="button" onClick={() => { setMode('quick'); setStep('questions'); setCurrentQuestionIdx(0); setAnswers([]); }}
                className="w-full text-left p-3 rounded-lg border border-border hover:border-[hsl(160,35%,45%)] hover:bg-[hsl(160,35%,45%)/0.05] transition-colors">
                <p className="text-sm font-medium text-foreground">{c.quickMode}</p>
                <p className="text-xs text-muted-foreground">{c.quickDesc}</p>
              </button>

              {/* Deep Unlock - Connection+ */}
              <button type="button" onClick={() => {
                if (!isConnection) { toast.info(tc.deepUnlockOnly); return; }
                setMode('deep'); setStep('questions'); setCurrentQuestionIdx(0); setAnswers([]);
              }}
                className={cn("w-full text-left p-3 rounded-lg border transition-colors", isConnection ? "border-[hsl(160,35%,45%)/0.5] bg-[hsl(160,35%,45%)/0.05] hover:bg-[hsl(160,35%,45%)/0.1]" : "border-border opacity-60")}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{c.deepMode}</p>
                    <p className="text-xs text-muted-foreground">{c.deepDesc}</p>
                  </div>
                  {!isConnection && <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">{c.connectionOnly}</span>}
                </div>
              </button>

              {/* Rewrite - only if user has text */}
              {userHasText && (
                <button type="button" onClick={() => { setMode('rewrite'); handleGenerate([]); }}
                  className="w-full text-left p-3 rounded-lg border border-border hover:border-[hsl(160,35%,45%)] hover:bg-[hsl(160,35%,45%)/0.05] transition-colors">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2"><Pencil className="h-3.5 w-3.5" />{c.rewriteMode}</p>
                  <p className="text-xs text-muted-foreground">{c.rewriteDesc}</p>
                </button>
              )}
            </div>

            {/* Length selector */}
            <div className="pt-2 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">{c.length}</p>
              <div className="flex flex-wrap gap-1.5">
                {LENGTH_OPTIONS.map(opt => {
                  const disabled = opt.legacyOnly && !isLegacy;
                  return (
                    <button key={opt.value} type="button" disabled={disabled}
                      onClick={() => setLengthOption(opt.value)}
                      className={cn("text-xs px-3 py-1.5 rounded-full border transition-colors",
                        lengthOption === opt.value ? "bg-[hsl(160,35%,45%)] text-white border-[hsl(160,35%,45%)]" : "border-border hover:border-[hsl(160,35%,45%)/0.5]",
                        disabled && "opacity-40 cursor-not-allowed"
                      )}>
                      {lstr(opt.labels, lang)} <span className="text-[10px] opacity-70">({opt.range} {c.words})</span>
                      {opt.legacyOnly && !isLegacy && <span className="ml-1 text-[9px]">🔒</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <Button type="button" variant="ghost" onClick={() => setStep('intro')} className="w-full text-muted-foreground text-sm">
              {c.goBack}
            </Button>
          </motion.div>
        )}

        {/* QUESTIONS */}
        {step === 'questions' && (
          <motion.div key="questions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="bg-background/60 dark:bg-background/30 rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">{c.questionOf} {currentQuestionIdx + 1} {c.of} {maxQuestions}</p>
              <p className="text-sm text-foreground leading-relaxed font-medium">
                {questions[currentQuestionIdx]}
              </p>
            </div>

            <Textarea
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              placeholder={c.placeholder}
              className="min-h-[100px] resize-none"
            />

            <div className="flex justify-between gap-2">
              <Button type="button" variant="ghost" onClick={() => {
                if (currentQuestionIdx > 0) { setCurrentQuestionIdx(prev => prev - 1); setCurrentAnswer(answers[currentQuestionIdx - 1] || ''); setAnswers(prev => prev.slice(0, -1)); }
                else setStep('mode_select');
              }}>
                {c.goBack}
              </Button>
              <div className="flex gap-2">
                {currentQuestionIdx > 0 && (
                  <Button type="button" variant="outline" onClick={() => handleGenerate([...answers])}>
                    {c.skip}
                  </Button>
                )}
                <Button type="button" onClick={handleAnswerSubmit} disabled={!currentAnswer.trim()}
                  className="bg-[hsl(160,35%,45%)] hover:bg-[hsl(160,35%,40%)] text-white">
                  {c.keepGoing}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* GENERATING */}
        {step === 'generating' && (
          <motion.div key="generating" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-8 text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[hsl(160,35%,45%)]" />
            <p className="text-sm text-muted-foreground">{c.generating}</p>
          </motion.div>
        )}

        {/* DRAFTS */}
        {step === 'drafts' && (
          <motion.div key="drafts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            {/* Draft tabs if multiple */}
            {drafts.length > 1 && (
              <div className="flex gap-2">
                {drafts.map((_, i) => (
                  <button key={i} type="button" onClick={() => setSelectedDraft(i)}
                    className={cn("text-xs px-3 py-1.5 rounded-full border transition-colors",
                      selectedDraft === i ? "bg-[hsl(160,35%,45%)] text-white border-[hsl(160,35%,45%)]" : "border-border")}>
                    {i === 0 ? c.warmSimple : c.deepReflective}
                  </button>
                ))}
              </div>
            )}

            {/* Draft preview */}
            <div className="bg-background/60 dark:bg-background/30 rounded-lg p-4 max-h-[300px] overflow-y-auto">
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                {drafts[selectedDraft]}
              </p>
            </div>

            <p className="text-xs text-muted-foreground text-center italic">{c.editNote}</p>

            {/* Insert options */}
            <div className="flex justify-center">
              <Button type="button" variant="default" size="sm" className="px-6" onClick={() => handleInsertDraft(0)}>
                {USE_DRAFT_LABEL[lang] ?? USE_DRAFT_LABEL.en}
              </Button>
            </div>

            {/* Refine options */}
            <div className="pt-2 border-t border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-3.5 w-3.5 text-[hsl(160,35%,45%)]" />
                <p className="text-xs text-muted-foreground">{c.refine}</p>
                {isStarter && <span className="text-[10px] text-muted-foreground">({c.starterLimit})</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(isStarter
                  ? (REFINE_OPTIONS[lang] ?? REFINE_OPTIONS.en).slice(0, 1)
                  : (REFINE_OPTIONS[lang] ?? REFINE_OPTIONS.en)
                ).map(opt => (
                  <button key={opt} type="button" onClick={() => handleRefine(opt)} disabled={isGenerating}
                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-[hsl(160,35%,45%)/0.5] hover:bg-[hsl(160,35%,45%)/0.05] transition-colors disabled:opacity-50">
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
